import { foldRangeFilter } from '@features/ui-filters/api/rangeFilter';
import { Primitive, useUrlFilters } from '@root/src/hooks/useUrlFilters';
import { useUrlPagination } from '@root/src/hooks/useUrlPagination';
import { useUrlSort } from '@root/src/hooks/useUrlSort';
import { SortOrder } from '@root/src/types/common';
import { useMemo } from 'react';

export type SstFilterValues = {
  'spaceObject.name': string;
  entity: string[];
  'spaceObject.cosparId': string;
  'spaceObject.noradId': string;
  'spaceObject.category': string;
  'spaceObject.orbit': string;
  sstType: string;
  createdAtMin: string;
  createdAtMax: string;
};

const defaultFilters: SstFilterValues = {
  'spaceObject.name': '',
  entity: [],
  'spaceObject.cosparId': '',
  'spaceObject.noradId': '',
  'spaceObject.category': '',
  'spaceObject.orbit': '',
  sstType: '',
  createdAtMin: '',
  createdAtMax: '',
};

const defaultSort = '-createdAt';

export const useSstUrlState = (): {
  filters: SstFilterValues;
  apiFilters: Record<string, Primitive | Primitive[]>;
  hasActiveFilters: boolean;
  page: number;
  sort: {
    field: string;
    order: SortOrder;
  };
  handleFilterChange: (field: keyof SstFilterValues, value: string | string[]) => void;
  handlePageChange: (page: number) => void;
  handleSortChange: (field: string, order: SortOrder) => void;
  resetSearch: () => void;
} => {
  const {
    filters,
    apiFilters: urlApiFilters,
    hasActiveFilters,
    setFilterField,
    resetFilters,
  } = useUrlFilters({
    defaultFilters,
    debounce: 300,
  });
  const apiFilters = useMemo(
    () => foldRangeFilter(urlApiFilters, { min: 'createdAtMin', max: 'createdAtMax', into: 'createdAt' }),
    [urlApiFilters],
  );

  const { page, setPage, resetPage } = useUrlPagination(1);
  const { sort, setSort, resetSort } = useUrlSort(defaultSort);

  const handleFilterChange = (field: keyof SstFilterValues, value: string | string[]) => {
    setFilterField(field, value as Primitive | Primitive[]);
    resetPage();
  };

  const handleSortChange = (field: string, order: SortOrder) => {
    setSort(field, order);
    resetPage();
  };

  const resetSearch = () => {
    resetFilters();
    resetPage();
    resetSort();
  };

  return {
    filters,
    apiFilters,
    hasActiveFilters,
    page,
    sort,
    handleFilterChange,
    handlePageChange: setPage,
    handleSortChange,
    resetSearch,
  };
};
